import { Analytics } from '@cobliteam/analytics';
import useDriver from '@cobliteam/hooks/src/useEntity/useDriver';
import useFuscaVehicle from '@cobliteam/hooks/src/useEntity/useFuscaVehicles';
import useVehicle from '@cobliteam/hooks/src/useEntity/useVehicle';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Typography from '@cobliteam/ui/components/Typography';
import Select, { SelectContent, SelectItem, SelectTrigger, SelectValue } from '@cobliteam/ui/form/Select';
import { formatVehicleLabel } from '@cobliteam/ui-business/components/Selects/SelectVehicle/utils';
import { Check } from 'lucide-react';
import { useMemo, useState } from 'react';

interface DriverAssociationStepBodyProps {
  fleetId?: string;
  /** Called after a vehicle is associated, to advance to the next step. */
  onCompleted?: () => void;
}

/** Linger after a successful association so the user sees the success state before advancing. */
const ADVANCE_DELAY_MS = 900;

/**
 * Inline "associate a driver to a vehicle" — sets the vehicle's driver (the same `driver`/`driverId`
 * that powers per-driver trip attribution and the maturity count), via the vehicle update. Both pickers
 * are the same dropdown (button) control. One association advances the guide.
 */
const DriverAssociationStepBody = ({ fleetId, onCompleted }: DriverAssociationStepBodyProps) => {
  const { t } = useTranslation('basic-value-onboarding');
  const [vehicleId, setVehicleId] = useState<string>('');
  const [driverId, setDriverId] = useState<string>('');
  const { data: vehicles } = useVehicle().getVehicles({});
  const { data: drivers } = useDriver().getDrivers();
  const availableVehicles = useMemo(() => (vehicles ?? []).filter((vehicle) => !vehicle.driverId), [vehicles]);
  const sortedDrivers = useMemo(() => (drivers ?? []).slice().sort((a, b) => a.name.localeCompare(b.name)), [drivers]);
  const { mutateAsync: updateVehicle, isPending, isSuccess } = useFuscaVehicle().updateVehicle;

  const handleAssociate = async () => {
    const vehicle = availableVehicles.find((item) => item.id === vehicleId);
    const driver = sortedDrivers.find((item) => item.id === driverId);
    if (!(vehicle && driver)) return;
    // Sets the vehicle's driver (driverId) — what trip reports and the maturity count actually read.
    await updateVehicle({ ...vehicle, newDriver: { id: driver.id, name: driver.name, phoneNumbers: driver.phoneNumbers ?? [] } });
    Analytics.track('BasicValueModalItemCreated', { fleetId, feature: 'driver-association' });
    window.setTimeout(() => onCompleted?.(), ADVANCE_DELAY_MS);
  };

  if (isSuccess) {
    return (
      <Typography variant='text-sm' className='flex items-center gap-1 text-success'>
        <Check size={16} aria-hidden />
        {t('association.associate_success')}
      </Typography>
    );
  }

  return (
    <div className='flex flex-col gap-2'>
      <Select value={vehicleId} onValueChange={setVehicleId}>
        <SelectTrigger>
          <SelectValue placeholder={t('inline.select_vehicle')} />
        </SelectTrigger>
        <SelectContent portal={false}>
          {availableVehicles.map((vehicle) => (
            <SelectItem key={vehicle.id} value={vehicle.id}>
              {formatVehicleLabel(vehicle)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className='flex items-end gap-2'>
        <Select value={driverId} onValueChange={setDriverId}>
          <SelectTrigger className='flex-1'>
            <SelectValue placeholder={t('association.select_driver')} />
          </SelectTrigger>
          <SelectContent portal={false}>
            {sortedDrivers.map((driver) => (
              <SelectItem key={driver.id} value={driver.id}>
                {driver.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button loading={isPending} disabled={!(vehicleId && driverId)} onClick={handleAssociate}>
          {t('associate')}
        </Button>
      </div>
    </div>
  );
};

export default DriverAssociationStepBody;
